import { Box } from '@mui/material';
import { useAddChallengeToPatient } from '_api/mutations';
import { AddChallengeToPatientPayload } from '_api/types';
import { SEVERITY } from '_constants/derived';
import { PrimaryButton, SecondaryButton } from 'components/atoms';
import { AlertMessage, BaseModal } from 'components/molecules';
import { AddChallengeToPatientProfileForm } from 'components/organisms/forms';
import {
  hasNonblankPatientInstructions,
  normalizePatientInstructions,
} from 'components/organisms/forms/patientInstructions';
import React, { useState } from 'react';
import { useChallenges } from 'utils/hooks';

import { buildChallengePayload } from './buildChallengePayload';
import { AddToPatientProfileModalProps } from './types';

export const AddChallengeToPatientProfileModal = ({
  open,
  onClose,
  patientId,
}: AddToPatientProfileModalProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutateAsync: addChallengeToPatient } = useAddChallengeToPatient();
  const { allChallenges } = useChallenges();
  const [payload, setPayload] = useState<AddChallengeToPatientPayload[]>([]);

  const close = () => {
    onClose();
    setPayload([]);
  };

  const updateList = async (ids: string[]) => {
    setPayload((current) => {
      let newPayload = [...current];
      ids.forEach((id) => {
        if (!newPayload.some((item) => item.contentId === Number(id))) {
          const challenge = allChallenges?.find((item) => item.id === Number(id));
          newPayload.push(buildChallengePayload(Number(id), patientId, challenge));
        }
      });

      newPayload = newPayload.filter((item) => ids.includes(item.contentId.toString()));

      return newPayload;
    });
  };

  const updateChallenge = async (key: string, value: any, id: number) => {
    setPayload((current) =>
      current.map((ch) => (ch.contentId === id ? { ...ch, [key]: value } : ch)),
    );
  };

  const onSubmit = () => {
    const newPayload: AddChallengeToPatientPayload[] = payload
      .filter((item) => item)
      .map((challenge) => ({
        ...challenge,
        instructionList: normalizePatientInstructions(challenge.instructionList),
      }));
    if (newPayload.some((challenge) => challenge.perWeek <= 0)) {
      setErrorMessage(
        "One more more challenges have a 'TIMES PER WEEK' have a value of 0. Please enter a value greater than 0.",
      );
    } else if (
      newPayload.some(
        (challenge) => !hasNonblankPatientInstructions(challenge.instructionList),
      )
    ) {
      setErrorMessage('Each challenge must have at least one instruction.');
    } else if (newPayload.length > 0) {
      addChallengeToPatient(newPayload as AddChallengeToPatientPayload[])
        .then(() => {
          setShowSuccess(true);
          setTimeout(() => close(), 1500);
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            setErrorMessage(
              'Unexpected error occured. Please close the modal and try again.',
            );
          } else if (error.message) {
            setErrorMessage(`ERROR: ${error.message}`);
          } else {
            setErrorMessage('Unexpected error. Please Try again.');
          }
          setPayload([]);
        });
    }
  };

  const Footer = () => {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          padding: 5,
          paddingBottom: 8,
          justifyContent: 'flex-end',
          gap: 2,
          mt: 2,
        }}
      >
        <SecondaryButton variant="outlined" color="primary" onClick={close}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={onSubmit}>Save</PrimaryButton>
      </Box>
    );
  };

  return (
    <BaseModal open={open} onClose={close} title={'Add To A Patient'} footer={Footer}>
      <AddChallengeToPatientProfileForm
        patientId={patientId}
        challengeList={payload}
        onSubmit={onSubmit}
        updateListOfObjects={updateList}
        updateObject={updateChallenge}
      />
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Successfully added to patient '}
      />
      <AlertMessage
        open={!!errorMessage && errorMessage.length > 0}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={errorMessage}
      />
    </BaseModal>
  );
};
